import { Box, Button, Typography } from '@mui/material';
import { ModalName } from 'app/enums';
import { UserEntity } from 'app/interfaces/entity';
import { modalsReducers } from 'app/store/modal/slice';
import { isArray } from 'lodash';
import { SearchResult } from 'modules/users/components/SearchUsersModal/SearchResult';
import { useSearchUsers } from 'modules/users/hooks';
import { searchUsersReducers } from 'modules/users/store/search-users/slice';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmptyPlaceholder } from 'shared/components/EmptyPlaceholder';
import { Modal } from 'shared/components/Modal';
import { SearchInput } from 'shared/components/SearchInput';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { sessionSelector } from 'app/store/session/selectors';
import { useStyles } from './SearchUsersModal.styles';

interface SearchUsersModalProps {
  onClose: (users: UserEntity[]) => void;
  activeUsers?: UserEntity[];
  hiddenUsers?: (UserEntity | Partial<UserEntity>)[];
  selectionLength?: number;
  buttonTitle?: string;
  description?: string;
  emptyPlaceholderText?: string;
}

const SearchUsersModalComponent: FC<SearchUsersModalProps> = ({
  onClose,
  activeUsers,
  selectionLength,
  hiddenUsers,
  buttonTitle = 'Add Players',
  description = '',
  emptyPlaceholderText = 'Search for users above to add to your game.',
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const activeUser = useSelector(sessionSelector.activeUser);

  const withActiveUser = useMemo(() => {
    if (!isArray(hiddenUsers)) {
      return true;
    }

    return Boolean(hiddenUsers?.find((user) => user?.id !== activeUser?.id));
  }, [hiddenUsers, activeUser]);

  const [selectedUsers, setSelectedUsers] = useState<UserEntity[]>([]);
  const { changeHandler, loading, users } = useSearchUsers(activeUsers, withActiveUser);

  const filteredUsers = useMemo(() => {
    return users?.filter(
      (user) => (hiddenUsers ?? []).findIndex((hiddenUser) => hiddenUser?.id === user?.id) < 0
    );
  }, [hiddenUsers, users]);

  const onCloseHandler = useCallback(() => dispatch(searchUsersReducers.clearAll()), [dispatch]);

  const submitHandler = useCallback(() => {
    dispatch(searchUsersReducers.clearAll());
    onClose(selectedUsers as UserEntity[]);
    dispatch(modalsReducers.closeModal(ModalName.SearchUsers));
  }, [dispatch, onClose, selectedUsers]);

  const onUserSelectHandler = useCallback(
    (data: UserEntity[]) => {
      setSelectedUsers(data);
    },
    [setSelectedUsers]
  );

  return (
    <Modal
      maxWidth="md"
      title="Invite"
      name={ModalName.SearchUsers}
      contentHeight={680}
      withLogo={false}
      onClose={onCloseHandler}
      actions={
        <>
          <>{description && <Typography pb={1}>{description}</Typography>}</>
          <Button
            size="medium"
            color="primary"
            variant="contained"
            disabled={!selectedUsers?.length}
            onClick={submitHandler}
            className={classes.submitButton}
          >
            {buttonTitle}
          </Button>
        </>
      }
    >
      <Box className={classes.root}>
        <Box className={classes.inputContainer}>
          <SearchInput
            placeholder="Search for players…"
            focusOnInit={true}
            onChange={changeHandler}
            loading={loading}
          />
        </Box>
        <Box className={classes.searchResult}>
          {filteredUsers?.length || selectedUsers?.length ? (
            <SearchResult
              onUserSelect={onUserSelectHandler}
              activeUsers={activeUsers}
              users={filteredUsers}
              selectionLength={selectionLength}
            />
          ) : (
            <EmptyPlaceholder text={emptyPlaceholderText} />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export const SearchUsersModal = withModalOpenChecker(
  SearchUsersModalComponent,
  ModalName.SearchUsers
);
